import React, { useState } from 'react';
import { Heart, Send, User, Phone, MessageCircle } from 'lucide-react';

const PrayerRequest = () => {
  const [formData, setFormData] = useState({
    prayerType: '',
    name: '',
    contactNumber: '',
    details: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const prayerTypes = [
    'Blessings',
    'Healing Prayer',
    'Memorial',
    'Wedding',
    'Departed',
    'Incense Prayer'
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const api = (await import('../utils/api')).default;
      
      await api.post('/api/prayer-request/submit', formData);
      
      setSubmitStatus('success');
      setFormData({
        prayerType: '',
        name: '',
        contactNumber: '',
        details: ''
      });
    } catch (error) {
      console.error('Error submitting prayer request:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-amber-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-4">
            <Heart className="w-12 h-12 text-red-600 mr-3" />
            <h1 className="text-4xl font-bold text-gray-900">Prayer Request</h1>
          </div>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            We believe in the power of prayer. Submit your prayer request and our community will pray for you and your loved ones.
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Prayer Type Dropdown */}
            <div>
              <label htmlFor="prayerType" className="block text-sm font-medium text-gray-700 mb-2">
                Prayer Type *
              </label>
              <select
                id="prayerType"
                name="prayerType"
                value={formData.prayerType}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors"
              >
                <option value="">Select prayer type</option>
                {prayerTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>

            {/* Name Field */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                <User className="w-4 h-4 inline mr-1" />
                Name of the person *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors"
                placeholder="Enter the name of the person"
              />
            </div>

            {/* Contact Number Field */}
            <div>
              <label htmlFor="contactNumber" className="block text-sm font-medium text-gray-700 mb-2">
                <Phone className="w-4 h-4 inline mr-1" />
                Contact Number (Optional)
              </label>
              <input
                type="tel"
                id="contactNumber"
                name="contactNumber"
                value={formData.contactNumber}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors"
                placeholder="Enter contact number"
              />
            </div>

            {/* Details Field */}
            <div>
              <label htmlFor="details" className="block text-sm font-medium text-gray-700 mb-2">
                <MessageCircle className="w-4 h-4 inline mr-1" />
                Details about the prayer request *
              </label>
              <textarea
                id="details"
                name="details"
                value={formData.details}
                onChange={handleChange}
                required
                rows="6"
                className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors resize-vertical"
                placeholder="Please provide details about your prayer request..."
              />
            </div>

            {/* Submit Button */}
            <div className="flex justify-center">
              <button
                type="submit"
                disabled={isSubmitting}
                className="inline-flex items-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Submit Prayer Request
                  </>
                )}
              </button>
            </div>

            {/* Status Messages */}
            {submitStatus === 'success' && (
              <div className="text-center p-4 bg-green-50 border border-green-200 rounded-md">
                <p className="text-green-800 font-medium">
                  Your prayer request has been submitted successfully. We will keep you and your loved ones in our prayers.
                </p>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="text-center p-4 bg-red-50 border border-red-200 rounded-md">
                <p className="text-red-800 font-medium">
                  There was an error submitting your prayer request. Please try again.
                </p>
              </div>
            )}
          </form>
        </div>

        {/* Additional Information */}
        <div className="mt-12 bg-blue-50 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-blue-900 mb-3">Prayer Guidelines</h3>
          <div className="text-blue-800 space-y-2">
            <p>• All prayer requests will be treated with confidentiality and respect.</p>
            <p>• Our prayer group meets regularly to pray for all submitted requests.</p>
            <p>• If you need immediate pastoral care, please contact our church office directly.</p>
            <p>• We encourage you to also pray for others who have submitted requests.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrayerRequest;